import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Container } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../common_components/ConfirmationModal';

const ProductBrandModal = ({ show, handleClose, brand = null }) => {
    console.log('Brand:', JSON.stringify(brand, null, 2));
    const [brandName, setBrandName] = useState('');
    const [description, setDescription] = useState('');
    const [website, setWebsite] = useState('');
    
    const [inputValue, setInputValue] = useState('');
    const [isOwner, setIsOwner] = useState(false);

    const token = localStorage.getItem('token');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    useEffect(() => {
        if (brand) {
            setBrandName(brand.name);
            setDescription(brand.description);
            setWebsite(brand.website);
            setIsOwner(brand.is_owner);
        } else {
            setBrandName('');
            setDescription('');
            setWebsite('');
            setIsOwner(false);
        }
    }, [brand]);

    const handleBrandChange = (selectedOption) => {
        if (selectedOption) {
            setBrandName(selectedOption.value);
        } else {
            setBrandName(inputValue);
        }
    };

    const handleInputChange = newValue => {
        setInputValue(newValue);
        return newValue;
    };

    const formatWebsiteUrl = (url) => {
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`;
        }
        return url;
    };

    const fetchProductBrands = async (inputValue = '') => {
        if (isOwner) {
            return []; // Prevent fetching brands if the user is the owner
        }
        console.log("Triggeing fetchProductBrands through the modal");
        const params = { search: inputValue };
        const url = `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`;

        try {
            const response = await axios.get(url, {
                params,
                headers: {
                    'Authorization': `Token ${token}`
                },
            });
            return response.data.results.map(brand => ({
                label: brand.name,
                value: brand.name,
            }));
        } catch (error) {
            console.error('Error fetching brands:', error);
            return [];
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!brandName.trim() || !description.trim()) {
            toast.error('Both brand name and description are required.');
            return;
        }

        const formattedWebsite = formatWebsiteUrl(website);

        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`, {
                params: { search: brandName },
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            const brands = response.data.results;
            if (brands.some(brand => brand.name.toLowerCase() === brandName.toLowerCase())) {
                toast.error('Brand already exists.');
                return;
            }

            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`, {
                name: brandName,
                description: description,
                website: formattedWebsite,
                status: "ACTIVE"
            }, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            toast.success('Brand added successfully!');
            handleClose();
            setBrandName('');
            setDescription('');
            setWebsite('');
        } catch (error) {
            console.error('Error adding new brand:', error);
            toast.error('Failed to add brand. Please try again.');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        if (!brandName.trim() || !description.trim()) {
            toast.error('Both brand name and description are required.');
            return;
        }

        const formattedWebsite = formatWebsiteUrl(website);

        try {
            await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/${brand.id}/`, {
                name: brandName,
                description: description,
                website: formattedWebsite,
                status: brand.status
            }, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            toast.success('Brand updated successfully!');
            handleClose();
        } catch (error) {
            console.error('Error updating brand:', error);
            toast.error('Failed to update brand. Please try again.');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/${brand.id}/`, {
                headers: {
                    'Authorization': `Token ${token}`
                }
            });

            toast.success('Brand deleted successfully!');
            closeDeleteConfirmation(); // Close the confirmation modal
            handleClose(); // Close the main modal
        } catch (error) {
            console.error('Error deleting brand:', error);
            toast.error('Failed to delete brand. Please try again.');
        }
    };

    const showDeleteConfirmation = () => {
        setShowConfirmationModal(true);
    };

    const closeDeleteConfirmation = () => {
        setShowConfirmationModal(false);
    };

    return (
        <Container>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{brand ? brand.name : 'Add New Product Brand'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={isOwner ? handleUpdate : handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Brand Name</Form.Label>
                            <AsyncSelect
                                cacheOptions
                                placeholder="Enter brand name"
                                loadOptions={fetchProductBrands}
                                defaultOptions
                                onInputChange={handleInputChange}
                                onChange={handleBrandChange}
                                inputValue={inputValue}
                                value={brandName ? { label: brandName, value: brandName } : { label: inputValue, value: inputValue }}
                                onBlur={() => setBrandName(inputValue)}
                                isDisabled={!!brand && !isOwner} // Disable input if viewing an existing brand and not owner
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder={isOwner || description ? "Enter brand description" : "Brand description not available"}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={!!brand && !isOwner} // Disable input if viewing an existing brand and not owner
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Website</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={isOwner || website ? "Enter brand website" : "Brand website not available"}
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                onBlur={() => setWebsite(formatWebsiteUrl(website))}
                                disabled={!!brand && !isOwner} // Disable input if viewing an existing brand and not owner
                            />
                        </Form.Group>

                        {!brand && (
                            <Button variant="primary" type="submit">
                                Add Brand
                            </Button>
                        )}

                        {brand && isOwner && (
                            <>
                                <Button variant="primary" type="submit">
                                    Update Brand
                                </Button>
                                <Button variant="danger" onClick={showDeleteConfirmation} style={{ marginLeft: '10px' }}>
                                    Delete Brand
                                </Button>
                            </>
                        )}
                    </Form>
                </Modal.Body>
            </Modal>
            <ConfirmationModal
                show={showConfirmationModal}
                onHide={closeDeleteConfirmation}
                onConfirm={handleDelete}
                title="Remove this brand?"
                body="Are you sure you want to remove this brand?"
            />
        </Container>
    );
};

export default ProductBrandModal;
